import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
@Injectable({
  providedIn: 'root',
})
export class ContentResizeService {
  private renderer: Renderer2;

  constructor(private rendererFactory: RendererFactory2) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  initResizing(containerSelector: string = '.content-box'): void {
    const containers = document.querySelectorAll(containerSelector);
    if (containers.length === 0) return;
    containers.forEach((container: Element) => {
      this.initRowResizing(container.querySelector('.content-box-row'));
    });
  }

  private initRowResizing(containerSelector: Element): void {
    const container = containerSelector;
    if (!container) return;
    container.querySelectorAll('.content-resize-handle').forEach(handle => handle.remove());
    // Get only visible rowItems
    const rowItemContainers = Array.from(container.children).filter((child: HTMLElement) => child.offsetParent !== null); // offsetParent == null means hidden

    if (rowItemContainers.length <= 1) return;

    rowItemContainers.forEach((element: Element, index: number) => {
      const isLast = index === rowItemContainers.length - 1;
      const next = rowItemContainers[index + 1] as HTMLElement | undefined;

      // Create handle only if not last and next item is visible
      const nextIsHidden = !next || next.offsetParent === null;
      if (isLast || nextIsHidden) return; // No handle on the last item

      // Avoid duplicate handle
      if (element.querySelector('.content-resize-handle')) return;

      // Create and append
      const handle = this.renderer.createElement('div');
      this.renderer.addClass(handle, 'content-resize-handle');
      this.renderer.appendChild(element, handle);

      this.attachResizeLogic(handle, element, container);
    });
  }

  private attachResizeLogic(handle: HTMLElement, rowItem: Element, container: Element): void {
    this.renderer.listen(handle, 'mousedown', (e: MouseEvent) => {
      const nextSibling = rowItem.nextElementSibling;
      if (!nextSibling) return;
      
      const containerWidth = container.clientWidth;
      const startX = e.clientX;
      const startWidth = rowItem.clientWidth;
      const startNextWidth = nextSibling.clientWidth;

      this.renderer.addClass(container, 'disable-select');
      let rafId: number | null = null;
      const onMouseMove = (moveEvent: MouseEvent) => {
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          const dx = moveEvent.clientX - startX;
          const newWidth = ((startWidth + dx) / containerWidth) * 100;
          const newNextWidth = ((startNextWidth - dx) / containerWidth) * 100;

          if (newWidth >= 10 && newNextWidth >= 10) {
            (rowItem as HTMLElement).style.width = `${newWidth}%`;
            (nextSibling as HTMLElement).style.width = `${newNextWidth}%`;
          }

          rowItem.setAttribute('data-resized', 'true');
        });
      };

      const onMouseUp = () => {
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
        this.renderer.removeClass(container, 'disable-select');
      };

      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    });
  }

  hideHandleOnRelation(container: NodeListOf<HTMLElement>): void {
    const rowItems = Array.from(container) as HTMLElement[];

    rowItems.forEach((rowItem, index) => {
      const isLast = index === rowItems.length - 1;
      const next = rowItems[index + 1];
      const nextIsHidden = next?.classList.contains('has-relation') && getComputedStyle(next).display === 'none';

      const handle = rowItem.querySelector('.content-resize-handle');
      if (handle) {
        if (isLast || nextIsHidden) {
          this.renderer.addClass(handle, 'hidden');
        } else {
          this.renderer.removeClass(handle, 'hidden');
        }
      }
    });
  }
}
